import * as React from "react";
import { Base } from "../../../../framework/base";
import { ClickAwayListener, Divider, MenuItem, Select, SxProps, Theme, Tooltip } from "@mui/material";

export interface IDropdownAction {
    disabled?: boolean;
    classes?: string;
    divider?: boolean;
    selected?: boolean;
    icon?: React.ReactNode;
    title: string | React.ReactNode;
    textTitle?: string;
    onClick: () => void;
}

// DropdownMenu
// ***********************************************************************************************************************
export interface IDropdownMenuProp {
    actions: IDropdownAction[];
    menuRight?: boolean;
    selectMode?: boolean;
    onToggleDropodown: () => void;
}

export class DropdownMenu extends React.Component<IDropdownMenuProp, {}> {
    private dropdownMenuDiv: HTMLDivElement;

    scrollToActiveMenuItem = () => {
        if (!this.props.selectMode || !this.dropdownMenuDiv) return;
        const elements = this.dropdownMenuDiv.getElementsByClassName("selected");
        if (elements.length > 0 && elements[0]) {
            const y = (elements[0] as HTMLDivElement).offsetTop;
            this.dropdownMenuDiv.scrollTo({
                top: y,
                left: 0,
                behavior: "auto"
            });
        }
    };

    componentDidMount(): void {
        this.scrollToActiveMenuItem();
    }

    handleActionClick = (ev: React.MouseEvent, action: IDropdownAction) => {
        ev.preventDefault();
        if (!action) return;
        if (action.disabled) return;
        this.props.onToggleDropodown();
        action.onClick();
    };
    
    render() {
        const props = this.props;
        const actions: (string | React.ReactNode)[] = [];
        for (let i = 0; i < props.actions.length; i++) {
            const action = props.actions[i];
            if (action.divider) {
                actions.push(<Divider key={i}>{action.title}</Divider>);
                continue;
            }
            if (action.onClick === null) {
                continue;
            }
            actions.push(
                <MenuItem
                    key={i}
                    onClick={(ev) => this.handleActionClick(ev, action) }
                    selected={action.selected}
                    sx={{
                        "textDecoration": action.disabled ? "line-through" : "none",
                        "&:hover": {
                            textDecoration: action.disabled ? "line-through" : "none",
                        }
                    }}
                >
                    {action.title}
                </MenuItem>
            );
        }
        return (
            <>{ actions }</>
        );
    }
}

// Dropdown
// ***********************************************************************************************************************
export interface IDropdownProp {
    index?: number;
    autoFocus?: boolean;
    autoHeight?: boolean;
    menuRight?: boolean;
    classes?: string;
    buttonClasses?: string;
    disabled?: boolean;
    selectMode?: boolean;
    selectedTitle: string | React.ReactNode;
    startIcon?: React.ReactNode;
    sx?: SxProps<Theme>;
    tooltip?: string;
    actions: IDropdownAction[];
}

export interface IDropdownState {
    open: boolean;
}

export class Dropdown extends React.Component<IDropdownProp, IDropdownState> {
    _isMounted: boolean;
    dropdownId: string;

    constructor(props: IDropdownProp) {
        super(props);
        this.dropdownId = Base.getGuid();
        this.state = {
            open: false
        };
    }

    dropdownClickEvent = (ev: MouseEvent) => {
        if (!this._isMounted) return;
        const element = ev.target as HTMLElement;
        const multiSelect = element.closest("[data-id='" + this.dropdownId + "']");
        if (!multiSelect) {
            this.setState({
                open: false
            });
        }
    };

    handleKeyPress = (e) => {
        const props = this.props;
        const key: string = e.key;
        if (Base.isNullOrUndefined(key)) return;
        let firstMatchBeforeSelected: IDropdownAction = null;
        let firstMatchAfterSelected: IDropdownAction = null;
        let selectedFound = false;
        for (const action of props.actions) {
            if (action.disabled) continue;
            const title = typeof action.title === "string" ? action.title : action.textTitle;
            if (!title || title.length < 1) continue;
            if (!selectedFound && !firstMatchBeforeSelected && title[0].equalIgnoreCase(key)) {
                firstMatchBeforeSelected = action;
            }
            if (action.selected) {
                selectedFound = true;
                continue;
            }
            if (!selectedFound) continue;
            if (title[0].equalIgnoreCase(key)) {
                firstMatchAfterSelected = action;
                break;
            }
        }
        const firstMatch = firstMatchAfterSelected ?? firstMatchBeforeSelected;
        if (!firstMatch) return;
        firstMatch.onClick();
        return true;
    };

    handleToggleDropdown = () => {
        if (this.props.disabled) return;
        this.setState({
            open: !this.state.open
        });
    };

    handleDropdownClose = () => {
        this.setState({ open: false });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const disabled = !!props.disabled;
        return (
            <ClickAwayListener onClickAway={this.handleDropdownClose}>
                <Tooltip title={props.tooltip}>
                    <Select
                        sx={props.sx}
                        startAdornment={props.startIcon}
                        open={state.open}
                        name={this.dropdownId}
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        renderValue={() => props.selectedTitle}
                        autoFocus={props.autoFocus}
                        onClick={this.handleToggleDropdown}
                        onKeyPress={props.selectMode ? this.handleKeyPress : null}
                        disabled={disabled}
                        size="small"
                        fullWidth
                    >
                        <DropdownMenu
                            actions={props.actions}
                            menuRight={props.menuRight}
                            selectMode={props.selectMode}
                            onToggleDropodown={this.handleToggleDropdown}
                        />
                    </Select>
                </Tooltip>
            </ClickAwayListener>
        );
    }
}